import React, { useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";

/*
 * RAIL GROUP: the shell every reference group sits in (Deal and money, People,
 * Fit, Their LinkedIn and intake, Behind the scenes).
 *
 * The rail is reference, not news, so each group is first of all ONE LINE: its
 * title, an optional count badge, and a summary that carries the group's point
 * on its own ("Fee not signed yet · their budget $10K/mo"), with ▸ / ▾. The
 * situation's key group starts open (SITUATION_MATRIX, via railOrder in
 * situation.mjs); the rest start as their line. The summary stays on the head
 * when the group is open, as drawn, so opening never moves the line you read.
 *
 * On the phone a group never expands in place: its head opens a bottom sheet
 * with the same body (the brief: "Rail groups open as bottom sheets").
 *
 * One Edit per card: `edit` puts it at the top right of the open body.
 *
 * Props:
 *   title        string, "Deal and money"
 *   count        number | null, the black badge after the title (People: 4)
 *   summary      string | node, the one line; shown open or closed
 *   defaultOpen  boolean, the starting state when uncontrolled (matrix "open")
 *   open         boolean | undefined, controlled open state
 *   onToggle     (nextOpen) => void, called on every head click
 *   edit         { label = "Edit", onClick } | null
 *   sheet        boolean, phone: the head opens a BottomSheet instead
 *   id           string, used for aria ids ("deal", "people")
 *   openSignal   number; each new value opens the group (the phone: its sheet).
 *                The phone bar's More opens the Prospect Room this way.
 *   children     the body, rendered only while open
 */
export default function RailGroup({
  title,
  count = null,
  summary = null,
  defaultOpen = false,
  open: controlled,
  onToggle = null,
  edit = null,
  sheet = false,
  id = null,
  openSignal = 0,
  children,
}) {
  const [own, setOwn] = useState(!!defaultOpen);
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => {
    if (!openSignal) return;
    if (sheet) setSheetOpen(true);
    else {
      setOwn(true);
      if (onToggle) onToggle(true);
    }
  }, [openSignal]);
  const isControlled = controlled !== undefined && controlled !== null;
  const open = sheet ? false : isControlled ? !!controlled : own;
  const bodyId = id ? `rec-group-${id}` : undefined;

  const toggle = () => {
    if (sheet) {
      setSheetOpen(true);
      return;
    }
    const next = !open;
    if (!isControlled) setOwn(next);
    if (onToggle) onToggle(next);
  };

  const body = (
    <div className="rec-group__body" id={bodyId}>
      {edit && (
        <div className="rec-group__edit">
          <button type="button" className="rec-link" onClick={edit.onClick}>{edit.label || "Edit"}</button>
        </div>
      )}
      {children}
    </div>
  );

  return (
    <section className="rec-group" data-group={id || undefined}>
      <button type="button" className="rec-group__head" onClick={toggle} aria-expanded={sheet ? sheetOpen : open} aria-controls={bodyId}>
        <span className="rec-group__title">{title}</span>
        {count !== null && count !== undefined && <span className="rec-count">{count}</span>}
        <span className="rec-group__summary">{summary}</span>
        <span className="rec-group__arrow" aria-hidden="true">{sheet ? "›" : open ? "▾" : "▸"}</span>
      </button>
      {open && body}
      {sheet && (
        <BottomSheet open={sheetOpen} title={title} onClose={() => setSheetOpen(false)}>
          {body}
        </BottomSheet>
      )}
    </section>
  );
}
